import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, Form, Input, Modal, Space, Table, DatePicker, message, Tag, Popconfirm, Dropdown, Upload, Progress, Select } from 'antd';
import { PlusOutlined, DeleteOutlined, ExportOutlined, ImportOutlined, UploadOutlined, SearchOutlined } from '@ant-design/icons';
import MediaSelector from '../components/MediaLibrary/MediaSelector';
import ColumnSelector from '../components/ColumnSelector';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import patentPaymentService, { PatentPayment, PatentPaymentGroup, PatentPaymentBatchItem } from '../services/patentPayment';
import mediaService, { MediaFile } from '../services/media';
import orderService from '../services/order';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import userPreferenceService from '../services/userPreference';
import usePermissions from '../hooks/usePermissions';
import { filterActionColumnByAccess } from '../utils/action-permissions';
import ListHelpButton from '../components/ListHelpButton';

// 表单值类型：编辑为单条；新增为批量 items
type PatentPaymentFormValues = Partial<PatentPayment> & {
  items?: Array<Partial<PatentPayment>>;
};

// 专利缴费管理页面：以 order_id 分组，子表展示同一订单的缴费记录
const PatentPaymentManagement: React.FC = () => {
  useDocumentTitle('专利年费监控');
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<PatentPaymentGroup[]>([]);
  const [createVisible, setCreateVisible] = useState(false);
  const [editing, setEditing] = useState<PatentPayment | null>(null);
  const [prefillOrderNo, setPrefillOrderNo] = useState<string | undefined>(undefined);
  const [form] = Form.useForm<PatentPaymentFormValues>();
  const [confirmForm] = Form.useForm<any>();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<PatentPayment | null>(null);
  const [urlParams] = useSearchParams();
  // 父表选择：按订单ID批量删除其全部缴费记录
  const [selectedGroupKeys, setSelectedGroupKeys] = useState<React.Key[]>([]);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [filters, setFilters] = useState<{ order_no: string; user_id: string }>({ order_no: '', user_id: '' });
  const [ownerOptions, setOwnerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [visibleParentColumns, setVisibleParentColumns] = useState<string[]>([]);
  const [visibleChildColumns, setVisibleChildColumns] = useState<string[]>([]);

  const { hasRole } = useAuth();
  const permissions = usePermissions();
  const canCreate = permissions.hasActionAccess('patent-payment-management', 'create');
  const canEdit = permissions.hasActionAccess('patent-payment-management', 'edit');
  const canDelete = permissions.hasActionAccess('patent-payment-management', 'delete');
  const canExport = permissions.hasActionAccess('patent-payment-management', 'export');
  const canImport = permissions.hasActionAccess('patent-payment-management', 'import');
  const hasParentRowActionAccess = canCreate || canDelete;
  const hasChildRowActionAccess = canEdit || canDelete;
  const isAdmin = hasRole('admin');
  const isManager = hasRole('sales_manager') || hasRole('coordinator_manager');

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await patentPaymentService.list({
        grouped: true,
        order_no: filters.order_no || undefined,
        user_id: filters.user_id ? Number(filters.user_id) : undefined,
      });
      if (res.success) {
        setGroups(res.data.groups || []);
        const opts: Array<{ value: number; label: string }> = [];
        (res.data.groups || []).forEach((g: any) => {
          (g.items || []).forEach((item: any) => {
            const uid = item?.user_id;
            if (uid) {
              const label = item?.owner_username || String(uid);
              opts.push({ value: Number(uid), label });
            }
          });
        });
        const uniqueMap: Record<number, string> = {};
        opts.forEach(o => { uniqueMap[o.value] = o.label; });
        const uniqueOpts = Object.entries(uniqueMap).map(([value, label]) => ({ value: Number(value), label }));
        setOwnerOptions(uniqueOpts);
      } else {
        message.error(res.message || '获取专利缴费数据失败');
      }
    } catch (e) {
      message.error('获取专利缴费数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  // 支持从订单页面带入 order_id 并自动打开新增Modal
  useEffect(() => {
    const openCreateFlag = urlParams.get('openCreate');
    const orderIdParam = urlParams.get('order_id');
    if (openCreateFlag && orderIdParam) {
      const oid = parseInt(orderIdParam, 10);
      if (!Number.isNaN(oid)) {
        openCreate(oid);
      }
    }
    // 仅在首次渲染时读取一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrderNoById = async (orderId: number) => {
    try {
      const res = await orderService.getOrder(orderId);
      if (res.success && res.data?.order_no) {
        setPrefillOrderNo(res.data.order_no);
        form.setFieldsValue({ order_no: res.data.order_no, order_id: orderId } as any);
      }
    } catch (e) {
      // 静默失败，交由用户输入
    }
  };

  const openCreate = (orderId?: number, orderNo?: string) => {
    setEditing(null);
    setPrefillOrderNo(orderNo);
    form.resetFields();
    form.setFieldsValue({
      order_id: orderId,
      order_no: orderNo,
      items: [{}],
    } as any);
    setCreateVisible(true);
    if (orderId && !orderNo) {
      fetchOrderNoById(orderId);
    }
  };

  const openEdit = (record: PatentPayment) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      ...record,
      started_at: record.started_at ? dayjs(record.started_at) : undefined,
      due_at: record.due_at ? dayjs(record.due_at) : undefined,
      grace_at: record.grace_at ? dayjs(record.grace_at) : undefined,
      payment_at: record.payment_at ? dayjs(record.payment_at) : undefined,
    } as any);
    setCreateVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields() as PatentPaymentFormValues;
      const toIso = (v: any) => (v ? dayjs(v as any).toDate().toISOString() : null);

      if (editing && editing.id) {
        // 单条编辑
        const payload: any = {
          ...values,
          started_at: toIso(values.started_at),
          due_at: toIso(values.due_at),
          grace_at: toIso(values.grace_at),
          payment_at: toIso(values.payment_at),
        };
        // 编辑时不修改订单编号
        delete payload.order_id;
        const res = await patentPaymentService.update(editing.id, payload);
        if (res.success) {
          message.success('更新成功');
          setCreateVisible(false);
          fetchData();
        } else {
          message.error(res.message || '更新失败');
        }
      } else {
        // 批量创建
        const orderId = values.order_id as number | undefined;
        const orderNo = (values as any).order_no as string | undefined;
        if (!orderNo && !orderId) {
          message.error('请填写订单编号');
          return;
        }
        const items: PatentPaymentBatchItem[] = (values.items || []).map((it) => ({
          year: String((it as any).year || ''),
          started_at: toIso((it as any).started_at),
          due_at: toIso((it as any).due_at),
          grace_at: toIso((it as any).grace_at),
          payment_at: toIso((it as any).payment_at),
          files: (it as any).files,
        }));
        const res = await patentPaymentService.createBatch({ order_id: orderId, order_no: orderNo, items });
        if (res.success) {
          message.success('批量添加成功');
          setCreateVisible(false);
          fetchData();
        } else {
          message.error(res.message || '批量创建失败');
        }
      }
    } catch (e) {
      const respMessage = (e as any)?.response?.data?.message;
      if (respMessage) {
        message.error(respMessage);
      }
      // 表单校验错误已由 antd 提示
    }
  };

  const handleDelete = async (record: PatentPayment) => {
    const res = await patentPaymentService.remove(record.id!);
    if (res.success) {
      message.success('删除成功');
      fetchData();
    } else {
      message.error(res.message || '删除失败');
    }
  };

  // 删除父级分组（该订单下的全部缴费记录）
  const handleDeleteGroup = async (orderId: number, items?: PatentPayment[]) => {
    try {
      // 优先使用传入的 items，避免依赖异步状态
      const sourceItems = Array.isArray(items) ? items : (groups.find(g => g.order_id === orderId)?.items || []);
      const ids = (sourceItems || []).map(i => i.id!).filter(Boolean);
      if (ids.length === 0) {
        message.info('该订单下暂无可删记录');
        return;
      }
      await Promise.all(ids.map(id => patentPaymentService.remove(id)));
      message.success(`已删除 ${ids.length} 条缴费记录`);
      fetchData();
    } catch (e) {
      message.error('删除失败，请稍后重试');
    }
  };

  // 导出数据
  const handleExport = async () => {
    try {
      const blob = await patentPaymentService.exportPatentPayments();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `专利缴费数据_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败');
    }
  };

  // 导入数据
  const handleImport = async (file: any) => {
    setImportLoading(true);
    setImportProgress(0);

    try {
      const result = await patentPaymentService.importPatentPayments(file);
      setImportProgress(100);

      if (result.success) {
        const { total = 0, success = 0, updated = 0, failed = 0, errors = [] } = result.data || {};
        let messageText = `导入完成！总计: ${total}, 成功: ${success}, 更新: ${updated}`;

        if (failed > 0) {
          messageText += `, 失败: ${failed}`;
          if (errors && errors.length > 0) {
            messageText += `\n错误详情: ${errors.slice(0, 3).join('; ')}`;
            if (errors.length > 3) {
              messageText += '...';
            }
          }
        }

        if (failed === 0) {
          message.success(messageText);
        } else {
          message.warning(messageText);
        }

        fetchData();
      } else {
        message.error(`导入失败: ${result.message || '未知错误'}`);
      }
    } catch (error) {
      console.error('导入失败:', error);
      message.error('导入失败，请检查文件格式');
    } finally {
      setImportLoading(false);
      setImportProgress(0);
      setImportModalVisible(false);
    }
  };

  // 父表格所有列定义
  const allParentColumnDefinitions: Record<string, any> = useMemo(() => ({
    nation: { title: '国家/地区', key: 'nation', width: 100, render: (_: any, row: any) => row.order_info?.nation || '-' },
    order_id: { title: '订单ID', dataIndex: 'order_id', key: 'order_id', width: 100 },
    order_no: { title: '订单编号', key: 'order_no', width: 140, render: (_: any, row: any) => row.order_info?.order_no || '-' },
    case_no: { title: '案件编号', key: 'case_no', width: 140, render: (_: any, row: any) => row.order_info?.case_no || '-' },
    applicant_name: { title: '申请人(中)', key: 'applicant_name', width: 160, render: (_: any, row: any) => row.order_info?.applicant_name || '-' },
    applicant_name_en: { title: '申请人(英)', key: 'applicant_name_en', width: 160, render: (_: any, row: any) => row.order_info?.applicant_name_en || '-' },
    count: { title: '期次数', key: 'count', render: (_: any, row: any) => row.items?.length || 0, width: 100 },
    actions: {
      title: '操作',
      key: 'actions',
      width: 240,
      fixed: 'right',
      render: (_: any, row: any) => {
        const canDeleteAll = (row.items?.length || 0) > 0;
        return (
          <Space size={8}>
            {canCreate && <Button size="small" type="primary" onClick={() => openCreate(row.order_id, row.order_info?.order_no || undefined)}>新增缴费</Button>}
            {canDelete && (
              <Popconfirm
                title="删除确认"
                description="确定删除该订单下的全部缴费记录？"
                okText="删除"
                okButtonProps={{ danger: true }}
                cancelText="取消"
                onConfirm={() => handleDeleteGroup(row.order_id, row.items)}
                disabled={!canDeleteAll}
              >
                <Button size="small" danger disabled={!canDeleteAll}>
                  删除全部
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      }
    }
  }), [canCreate, canDelete]);

  // 父表格默认显示列
  const defaultParentColumns = ['nation', 'order_id', 'order_no', 'case_no', 'applicant_name', 'applicant_name_en', 'count', 'actions'];

  // 根据可见列生成父表格列配置
  const parentColumns: ColumnsType<PatentPaymentGroup & { key: number }> = useMemo(() => {
    const columnsToShow = visibleParentColumns.length > 0 ? visibleParentColumns : defaultParentColumns;
    return filterActionColumnByAccess(
      columnsToShow.map(key => allParentColumnDefinitions[key]).filter(Boolean),
      hasParentRowActionAccess
    );
  }, [visibleParentColumns, allParentColumnDefinitions, hasParentRowActionAccess]);


  // 子表格所有列定义
  const allChildColumnDefinitions: Record<string, any> = useMemo(() => ({
    year: { title: '期次', dataIndex: 'year', key: 'year', width: 70 },
    started_at: { title: '开始日期', dataIndex: 'started_at', key: 'started_at', render: (v: any) => v ? dayjs(v).format('YYYY-MM-DD') : '-', width: 120 },
    due_at: { title: '截止日期', dataIndex: 'due_at', key: 'due_at', render: (v: any) => v ? dayjs(v).format('YYYY-MM-DD') : '-', width: 120 },
    grace_at: { title: '宽限日期', dataIndex: 'grace_at', key: 'grace_at', render: (v: any) => v ? dayjs(v).format('YYYY-MM-DD') : '-', width: 120 },
    phase: {
      title: '进度', key: 'phase', width: 100, render: (_: any, r: any) => {
        const now = dayjs();
        const started = r.started_at ? dayjs(r.started_at) : null;
        const due = r.due_at ? dayjs(r.due_at) : null;
        const grace = r.grace_at ? dayjs(r.grace_at) : null;

        if (grace && now.isAfter(grace)) {
          return <Tag color="red">已过期</Tag>;
        }
        if (due && now.isAfter(due)) {
          return <Tag color="orange">宽限中</Tag>;
        }
        if (started && now.isBefore(started)) {
          return <Tag>未开始</Tag>;
        }
        return <Tag color="blue">进行中</Tag>;
      }
    },
    status: { title: '状态', dataIndex: 'status', key: 'status', render: (v: boolean) => v ? <Tag color="green">已缴费</Tag> : <Tag color="red">未缴费</Tag>, width: 100 },
    payment_at: { title: '缴费日期', dataIndex: 'payment_at', key: 'payment_at', render: (v: any) => v ? dayjs(v).format('YYYY-MM-DD') : '-', width: 140 },
    remark: { title: '备注', dataIndex: 'remark', key: 'remark', width: 200 },
    files: {
      title: '缴费凭证', key: 'files', width: 200, render: (_: any, record: any) => (
        <MediaFilesCell files={(record as any).files} />
      )
    },
    actions: {
      title: '操作', key: 'actions', width: 320, render: (_: any, record: any) => (
        <Space size={8}>
          {canEdit && (
            <Button size="small" type="primary" onClick={() => {
              setConfirmTarget(record);
              confirmForm.resetFields();
              confirmForm.setFieldsValue({
                payment_at: record.payment_at ? dayjs(record.payment_at) : undefined,
                files: Array.isArray((record as any).files) ? (record as any).files : [],
                remark: (record as any).remark || ''
              });
              setConfirmVisible(true);
            }}>确认缴费</Button>
          )}
          {canEdit && <Button size="small" onClick={() => openEdit(record)}>编辑</Button>}
          {canDelete && (
            <Popconfirm
              title="删除确认"
              description="确定要删除该条缴费记录吗？"
              okText="删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={() => handleDelete(record)}
            >
              <Button size="small" danger>删除</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  }), [canEdit, canDelete, confirmForm]);

  // 子表格默认显示列
  const defaultChildColumns = ['year', 'started_at', 'due_at', 'grace_at', 'phase', 'status', 'payment_at', 'remark', 'files', 'actions'];

  // 根据可见列生成子表格列配置
  const childColumns: ColumnsType<PatentPayment> = useMemo(() => {
    const columnsToShow = visibleChildColumns.length > 0 ? visibleChildColumns : defaultChildColumns;
    return filterActionColumnByAccess(
      columnsToShow.map(key => allChildColumnDefinitions[key]).filter(Boolean),
      hasChildRowActionAccess
    );
  }, [visibleChildColumns, allChildColumnDefinitions, hasChildRowActionAccess]);

  // 初始化列设置
  useEffect(() => {
    const loadColumnSettings = async () => {
      try {
        const savedParentColumns = await userPreferenceService.getColumnSettings('patentPayments_parent');
        const savedChildColumns = await userPreferenceService.getColumnSettings('patentPayments_child');

        if (savedParentColumns && savedParentColumns.length > 0) {
          setVisibleParentColumns(savedParentColumns);
        } else {
          setVisibleParentColumns(defaultParentColumns);
        }

        if (savedChildColumns && savedChildColumns.length > 0) {
          setVisibleChildColumns(savedChildColumns);
        } else {
          setVisibleChildColumns(defaultChildColumns);
        }
      } catch (error) {
        console.warn('加载列设置失败:', error);
        setVisibleParentColumns(defaultParentColumns);
        setVisibleChildColumns(defaultChildColumns);
      }
    };

    loadColumnSettings();
  }, []);

  // 获取父表格可用列配置
  const availableParentColumns = filterActionColumnByAccess(Object.keys(allParentColumnDefinitions).map(key => ({
    key,
    title: allParentColumnDefinitions[key].title,
    fixed: allParentColumnDefinitions[key].fixed
  })), hasParentRowActionAccess);

  // 获取子表格可用列配置
  const availableChildColumns = filterActionColumnByAccess(Object.keys(allChildColumnDefinitions).map(key => ({
    key,
    title: allChildColumnDefinitions[key].title,
    fixed: allChildColumnDefinitions[key].fixed
  })), hasChildRowActionAccess);

  // 处理父表格列显示变更
  const handleParentColumnChange = async (checkedColumns: string[]) => {
    setVisibleParentColumns(checkedColumns);

    try {
      await userPreferenceService.saveColumnSettings('patentPayments_parent', checkedColumns);
    } catch (error) {
      console.error('保存列设置失败:', error);
      message.error('保存列设置失败');
    }
  };

  // 处理子表格列显示变更
  const handleChildColumnChange = async (checkedColumns: string[]) => {
    setVisibleChildColumns(checkedColumns);

    try {
      await userPreferenceService.saveColumnSettings('patentPayments_child', checkedColumns);
    } catch (error) {
      console.error('保存列设置失败:', error);
      message.error('保存列设置失败');
    }
  };


  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={(
          <Space size="small">
            <span>专利年费监控</span>
            <ListHelpButton page_key="patent_payment_management" />
          </Space>
        )}
        extra={
          <Space>
            <ColumnSelector
              availableColumns={availableParentColumns}
              visibleColumns={visibleParentColumns}
              onColumnChange={handleParentColumnChange}
              fixedColumns={[]}
              buttonLabel="列设置"
            />
            {/* <ColumnSelector
              availableColumns={availableChildColumns}
              visibleColumns={visibleChildColumns}
              onColumnChange={handleChildColumnChange}
              fixedColumns={['actions']}
              buttonLabel="缴费记录列设置"
            /> */}
            {canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate()}>新增缴费</Button>}

            {(() => {
              const items: any[] = [];
              if (canExport) items.push({ key: 'export', label: '导出数据', icon: <ExportOutlined />, onClick: handleExport });
              if (canImport) items.push({ key: 'import', label: '导入数据', icon: <ImportOutlined />, onClick: () => setImportModalVisible(true) });
              return items.length > 0 ? (
                <Dropdown menu={{ items }}>
                  <Button data-testid="btn-patent-data-menu">数据管理</Button>
                </Dropdown>
              ) : null;
            })()}

            {canDelete && selectedGroupKeys.length > 0 && (
              <Popconfirm
                title={`确定删除选中的 ${selectedGroupKeys.length} 个订单的全部缴费记录吗？`}
                okText="确定"
                cancelText="取消"
                onConfirm={async () => {
                  // 收集所有选中订单下的缴费记录 ID
                  const allIds: number[] = [];
                  const selectedOrderIds = (selectedGroupKeys as Array<string | number>)
                    .map(k => Number(k))
                    .filter(n => !Number.isNaN(n));
                  selectedOrderIds.forEach(orderId => {
                    const group = groups.find(g => g.order_id === orderId);
                    if (group?.items) {
                      group.items.forEach(item => {
                        if (item.id) allIds.push(item.id);
                      });
                    }
                  });
                  if (allIds.length === 0) {
                    message.info('选中的订单下暂无可删除的记录');
                    return;
                  }
                  try {
                    const response = await patentPaymentService.batchDelete(allIds);
                    if (response.success) {
                      message.success(response.message || `成功删除 ${response.deleted_count || allIds.length} 条缴费记录`);
                      setSelectedGroupKeys([]);
                      fetchData();
                    } else {
                      message.error(response.message || '批量删除失败');
                    }
                  } catch (e: any) {
                    message.error(e?.response?.data?.message || '批量删除失败');
                  }
                }}
                disabled={!selectedGroupKeys.length}
              >
                <Button danger disabled={!selectedGroupKeys.length} icon={<DeleteOutlined />}>批量删除</Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="订单编号"
            value={filters.order_no}
            allowClear
            prefix={<SearchOutlined />}
            onChange={(e) => setFilters(prev => ({ ...prev, order_no: e.target.value }))}
            style={{ width: 200 }}
          />
          {(isAdmin || isManager) && (
            <Select
              placeholder="所属销售"
              allowClear
              value={filters.user_id ? Number(filters.user_id) : undefined}
              onChange={(value) => setFilters(prev => ({ ...prev, user_id: value ? String(value) : '' }))}
              style={{ width: 200 }}
              options={ownerOptions}
            />
          )}
        </Space>

        {(filters.order_no || filters.user_id) && (
          <div style={{
            marginTop: 8,
            marginBottom: 16,
            padding: '8px 12px',
            backgroundColor: '#f0f2f5',
            borderRadius: 6,
          }}>
            <Space wrap>
              <span>已应用筛选条件：</span>
              {filters.order_no && (
                <Tag closable onClose={() => setFilters(prev => ({ ...prev, order_no: '' }))}>
                  订单编号: {filters.order_no}
                </Tag>
              )}
              {filters.user_id && (
                <Tag closable onClose={() => setFilters(prev => ({ ...prev, user_id: '' }))}>
                  销售: {ownerOptions.find(o => String(o.value) === filters.user_id)?.label || filters.user_id}
                </Tag>
              )}
              <Button type="link" size="small" onClick={() => setFilters({ order_no: '', user_id: '' })}>
                清除所有筛选
              </Button>
            </Space>
          </div>
        )}

        <Table
          loading={loading}
          rowKey={(row) => row.order_id}
          dataSource={groups.map((g) => ({ ...g, key: g.order_id }))}
          columns={parentColumns}
          scroll={{ x: 1200 }}
          sticky={{ offsetHeader: 0 }}
          rowSelection={canDelete ? {
            selectedRowKeys: selectedGroupKeys,
            onChange: (keys) => setSelectedGroupKeys(keys),
            preserveSelectedRowKeys: true,
          } : undefined}
          expandable={{
            expandedRowRender: (row) => (
              <Table
                rowKey={(r) => String(r.id)}
                dataSource={row.items}
                columns={childColumns}
                pagination={false}
                size="small"
              />
            )
          }}
        />

        <Modal
          title={editing ? '编辑缴费' : '新增缴费'}
          open={createVisible}
          onCancel={() => setCreateVisible(false)}
          onOk={handleSubmit}
          okText={editing ? '保存' : '添加'}
          cancelText="取消"
          width={960}
        >
          <Form form={form} layout="vertical">
            {!editing && (
              <>
                <Form.Item name="order_no" label="订单编号" rules={[{ required: true, message: '请输入订单编号' }]}>
                  <Input placeholder="请输入订单编号" disabled={!!prefillOrderNo} />
                </Form.Item>
                <Form.Item name="order_id" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
              </>
            )}

            {editing ? (
              // 单条编辑表单
              <>
                <Form.Item name="year" label="期次" rules={[{ required: true, message: '请输入年份' }]}>
                  <Input placeholder="例如 2025" />
                </Form.Item>
                <Form.Item name="started_at" label="开始日期">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="due_at" label="截止日期">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="grace_at" label="宽限日期">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="payment_at" label="缴费日期">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </>
            ) : (
              // 批量新增表单：表格样式，仅表头显示标签
              <Form.List name="items">
                {(fields, { add, remove }) => (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="ant-table" style={{ width: '100%', borderSpacing: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '8px' }}>期次</th>
                          <th style={{ padding: '8px' }}>开始日期</th>
                          <th style={{ padding: '8px' }}>截止日期</th>
                          <th style={{ padding: '8px' }}>宽限日期</th>
                          <th style={{ padding: '8px', textAlign: 'right' }}>
                            <Button type="dashed" size="small" onClick={() => add({})}>添加一行</Button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((field) => (
                          <tr key={field.key}>
                            <td style={{ padding: '8px', verticalAlign: 'top' }}>
                              <Form.Item {...field} name={[field.name, 'year']} rules={[{ required: true, message: '请输入年份' }]} style={{ marginBottom: 0 }}>
                                <Input placeholder="例如 2025" />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px', verticalAlign: 'top' }}>
                              <Form.Item {...field} name={[field.name, 'started_at']} style={{ marginBottom: 0 }}>
                                <DatePicker style={{ width: '100%' }} />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px', verticalAlign: 'top' }}>
                              <Form.Item {...field} name={[field.name, 'due_at']} style={{ marginBottom: 0 }}>
                                <DatePicker style={{ width: '100%' }} />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px', verticalAlign: 'top' }}>
                              <Form.Item {...field} name={[field.name, 'grace_at']} style={{ marginBottom: 0 }}>
                                <DatePicker style={{ width: '100%' }} />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right', verticalAlign: 'middle' }}>
                              <Button danger size="small" onClick={() => remove(field.name)}>删除</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Form.List>
            )}
          </Form>
        </Modal>

        <Modal
          title="导入专利缴费数据"
          open={importModalVisible}
          onCancel={() => {
            setImportModalVisible(false);
            setImportProgress(0);
          }}
          footer={null}
          width={500}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Upload.Dragger
              name="file"
              accept=".xlsx,.xls,.csv"
              beforeUpload={(file) => {
                handleImport(file);
                return false;
              }}
              showUploadList={false}
              disabled={importLoading}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">支持 .xlsx, .xls, .csv 格式文件，单个文件不超过10MB</p>
            </Upload.Dragger>

            {importLoading && (
              <div style={{ marginTop: 20 }}>
                <Progress percent={importProgress} />
                <p>导入中，请稍候...</p>
              </div>
            )}

            <div style={{ marginTop: 20, textAlign: 'left' }}>
              <ul style={{ paddingLeft: '20px', fontSize: '12px', color: '#666' }}>
                <li>请使用模板整理数据，订单编号或订单ID必须至少填写一项</li>
                <li>年份为必填项，日期字段建议使用 YYYY-MM-DD 格式</li>
                <li>“是否已缴费” 支持 是/否、已缴费/未缴费、1/0 等常见表达</li>
                <li>同一订单 + 年份重复将更新已有记录，否则新增新行</li>
              </ul>
            </div>
          </div>
        </Modal>

        <Modal
          title="确认缴费"
          open={confirmVisible}
          onCancel={() => setConfirmVisible(false)}
          onOk={async () => {
            try {
              const values = await confirmForm.validateFields();
              const toIso = (v: any) => (v ? dayjs(v as any).toDate().toISOString() : null);
              const files = Array.isArray(values.files) ? values.files : [];
              if (!confirmTarget?.id) return;
              const res = await patentPaymentService.update(confirmTarget.id, {
                payment_at: toIso(values.payment_at),
                files,
                remark: values.remark,
                status: true,
              });
              if (res.success) {
                message.success('已确认缴费');
                setConfirmVisible(false);
                setConfirmTarget(null);
                fetchData();
              } else {
                message.error(res.message || '确认失败');
              }
            } catch { }
          }}
          okText="确认"
          cancelText="取消"
          width={720}
        >
          <Form form={confirmForm} layout="vertical">
            <Form.Item name="payment_at" label="缴费日期" rules={[{ required: true, message: '请选择缴费日期' }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="files" label="缴费凭证" valuePropName="value">
              <MediaSelector multiple fileTypes={['document', 'image']} />
            </Form.Item>
            <Form.Item name="remark" label="备注">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default PatentPaymentManagement;

// 子组件：显示媒体文件标签并支持下载
const MediaFilesCell: React.FC<{ files?: number[] | null }> = ({ files }) => {
  const [mediaList, setMediaList] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const ids: number[] = Array.isArray(files) ? (files as number[]) : [];
        if (ids.length === 0) {
          setMediaList([]);
          return;
        }
        const results = await Promise.all(ids.map(id => mediaService.getMedia(Number(id))));
        const list: MediaFile[] = results.filter(r => r.success && r.data).map(r => r.data!);
        setMediaList(list);
      } catch (e) {
        setMediaList([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [JSON.stringify(files)]);

  if (loading) return <span style={{ color: '#999' }}>加载中...</span>;
  if (!mediaList || mediaList.length === 0) return <span style={{ color: '#999' }}>-</span>;

  return (
    <Space wrap>
      {mediaList.map((m) => (
        <Tag
          key={m.id}
          color="blue"
          style={{ cursor: 'pointer', marginBottom: 2 }}
          onClick={() => mediaService.downloadMedia(m.id, m.original_name || m.title)}
          title={`点击下载: ${m.original_name || m.title}`}
        >
          {(m.original_name || m.title).slice(0, 16)}
        </Tag>
      ))}
    </Space>
  );
};
